import React from 'react';
import { Box, Stack, styled, useTheme } from '@mui/material';

import type { SxProps } from '@mui/material';
export type SoundVisualizerVariant = 'both' | 'bar' | 'circle';
export interface SoundVisualizerProps {
  analyser: AnalyserNode | null;
  sensitivity?: number;
  decay?: number;
  variant?: SoundVisualizerVariant;
  bgColor?: string;
  defaultBarColor?: string;
  onClickRecord?: () => void;
  visualContainerStyle?: SxProps;
  visualBoxStyle?: SxProps;
}

export const SoundVisualizer = ({
  analyser,
  sensitivity = 3,
  decay = 0.5,
  variant = 'bar',
  bgColor,
  defaultBarColor = '#D8D8E2',
  visualContainerStyle = {},
  visualBoxStyle = {},
  onClickRecord,
}: SoundVisualizerProps) => {
  const theme = useTheme();
  const TOTAL_BARS = 17;
  const [filledBars, setFilledBars] = React.useState(0);

  React.useEffect(() => {
    if (!analyser) return;
    analyser.fftSize = 128;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    let prevVolume = 0;

    const update = () => {
      analyser.getByteFrequencyData(dataArray);
      const avg = dataArray.reduce((acc, v) => acc + v, 0) / dataArray.length;

      const scaled = Math.min(avg * sensitivity, 255);
      const smooth = prevVolume * decay + scaled * (1 - decay);
      prevVolume = smooth;

      setFilledBars(Math.round((smooth / 255) * TOTAL_BARS));
      requestAnimationFrame(update);
    };

    update();
    return () => setFilledBars(0);
  }, [analyser, sensitivity, decay]);

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      width="100%"
      position="relative"
      sx={{
        ...visualContainerStyle,
      }}
    >
      {(variant === 'bar' || variant === 'both') && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          p="6px 0 7.5px"
          width="100%"
          gap="11px"
          bgcolor={bgColor ?? theme.palette.grey[50]}
          zIndex={1}
        >
          {Array.from({ length: TOTAL_BARS }).map((_, i) => (
            <WaveBar
              key={i}
              isFilled={i < filledBars}
              defaultBarColor={defaultBarColor ?? theme.palette.grey[200]}
            />
          ))}
        </Box>
      )}
    </Stack>
  );
};

const WaveBar = styled(Box) <{ isFilled: boolean; defaultBarColor: string }>`
  width: 11px;
  height: 34px;
  border-radius: 11px;
  background-color: ${({ isFilled, defaultBarColor, theme }) =>
    isFilled ? theme.palette.primary.main : defaultBarColor};
  transition: background-color 0.1s ease-in-out;
`;

export default SoundVisualizer;
